/**
 * InlineAssigneePicker.tsx
 * Inline searchable Autocomplete for assigning employees to a ScheduleTask
 * directly from a row in the Task Schedule table.
 *
 * BUG-2026-0085 — replaces the small "+person" icon-button that previously
 * gated all assignment behind the AssignEmployeesDialog. The user feedback
 * was that a button to open a modal was slower than a visible dropdown.
 *
 * Behavior:
 *   - Shows currently-assigned employees as chips (deduped by UserKey).
 *   - Search input "Assign…" filters the assignable-user list by name.
 *   - On select, immediately creates EmployeeAssignment rows for that single
 *     user across the task's week-range via bulkAssignFromTask. Wave 3
 *     (percent retirement): an assignment is PRESENCE — every row writes 100
 *     (the server pins it); the old Tier-default percents are retired.
 *   - SkillGateError → the mentor picker opens inline (advisory since W2-8).
 *   - AbsenceDayConflictError (Q3B) → the server's message shows verbatim as
 *     a hard error; there is NO Save-anyway for assigning onto a day off.
 *   - Disabled when the task has no MachineBooking yet (consistent with the
 *     old button's disabled state — date range is unknown without bookings).
 *
 * BUG-2026-0093 — each currently-assigned chip now has an inline "×" that
 * removes that person from the task (soft-deletes all their per-week
 * EmployeeAssignment rows via unassignUserFromTask). Previously the chips were
 * display-only and removal was only reachable through the advanced dialog —
 * which itself never implemented it — so users had no way to unassign anyone.
 *
 * For changing per-week percents or assigning multiple people at once, the
 * user clicks the "advanced" link icon next to the dropdown — which opens the
 * existing dialog.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  listAssignableUsers,
  bulkAssignFromTask,
  weekOverloadHint,
  SkillGateError,
  AbsenceDayConflictError,
  type AssignableUser,
  type SkillGateBlockedUser,
  type EligibleMentor,
  type UserPercentEntry,
} from '../../../services/scheduleAssignments';
import { AssigneeOptionRow, useAssigneeCapacity } from '../../../components/schedule/assigneeOption';
import { formatCrossScenarioSource, type CrossScenarioSource } from '../../../services/scheduleEmployeeSchedule';
import { weekStartsInRange } from '../../../utils/weekUtils';
import { MentorRequiredModal } from '../../../components/schedule/MentorRequiredModal';
import {
  isQualifiedForTaskType,
  listHasQualifiedCandidate,
} from '../../../utils/scheduleQualification';
import { rankAssignees, isPickBlockedByAbsence } from '../../../utils/assigneeRanking';
import { CONTINUOUS_STAFFING_HINT } from '../../../utils/runModeRendering';
import {
  unassignUserFromTask,
  type TaskWithBookings,
  type TaskAssignee,
} from '../../../services/scheduleTasks';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { useRemovalGuard } from './useRemovalGuard';
import { onScheduleChange } from '../../../services/scheduleBus';

// Shared cache so every row hits the network at most once for the list.
// AssignEmployeesDialog already loads this list lazily; this picker mirrors
// that pattern but caches across rows to avoid N parallel requests when a
// long task table is rendered.
//
// BUG-2026-0090 — skill level is task-type-specific (SkillMatrix is keyed by
// UserKey × TaskTypeKey), so the list returned for one task type carries
// different SkillLevel values than another. The cache is therefore keyed by
// task type: rows sharing a task type reuse one fetch; rows of a different
// task type fetch their own list. Tasks with no task type use the NO_TASK_TYPE
// sentinel and never request skill levels.
const NO_TASK_TYPE = '__none__';

interface TaskTypeCacheEntry {
  users: AssignableUser[] | null;
  promise: Promise<AssignableUser[]> | null;
}

const userCache = new Map<string, TaskTypeCacheEntry>();

// The user list carries each person's SkillLevel for the task type, so a Skill
// Matrix edit (level change / clear) makes the cached qualification + level stale.
// Flush the cache on a 'skills-changed' bus event so the next dropdown open
// re-fetches fresh skill levels. One app-lifetime subscriber. (Gigi, 2026-06-22)
if (typeof window !== 'undefined') {
  onScheduleChange((c) => {
    if (c.kind === 'skills-changed') userCache.clear();
  });
}

function cacheEntry(key: string): TaskTypeCacheEntry {
  let entry = userCache.get(key);
  if (!entry) {
    entry = { users: null, promise: null };
    userCache.set(key, entry);
  }
  return entry;
}

/**
 * Load the assignable-user list for a given task type at most once.
 * `taskTypeKey === null` loads the plain list (no SkillLevel) under NO_TASK_TYPE.
 */
async function loadAssignableUsersOnce(
  taskTypeKey: string | null,
): Promise<AssignableUser[]> {
  const key = taskTypeKey ?? NO_TASK_TYPE;
  const entry = cacheEntry(key);
  if (entry.users) return entry.users;
  if (entry.promise) return entry.promise;
  entry.promise = listAssignableUsers(taskTypeKey)
    .then((users) => {
      entry.users = users;
      return users;
    })
    .finally(() => {
      entry.promise = null;
    });
  return entry.promise;
}

interface Props {
  task: TaskWithBookings;
  /** Currently-assigned rows for this task. May be empty. */
  assignees: TaskAssignee[];
  /** Called after a successful inline assignment so the parent can refresh. */
  onAssigned: () => void | Promise<void>;
  /**
   * BUG-2026-0093 — called after a successful inline removal so the parent can
   * refresh the row's chips (with a "removed" message instead of "assigned").
   */
  onUnassigned: () => void | Promise<void>;
  /**
   * Escalate to the AssignEmployeesDialog. The parent passes the same handler
   * already wired for the "advanced" icon button. When `preselectUser` is
   * provided, the dialog opens with that user pre-checked so the user can
   * resolve a SkillGate / Overbook conflict without re-picking. The third arg
   * carries this picker's machineBookingKey (079) so the dialog binds the
   * resolved assignment to the same machine.
   */
  onEscalateToDialog: (
    task: TaskWithBookings,
    preselectUser?: AssignableUser,
    machineBookingKey?: string | null,
  ) => void;
  /**
   * Per-machine staffing (079): when set, this picker staffs ONE machine of a
   * split task — created rows carry this MachineBookingKey and span that
   * booking's dates. Omit for a task-level assign (the Machine-Schedule table).
   */
  machineBookingKey?: string | null;
  /**
   * FEAT-2026-0007 6C.1 — scenario context.
   * When set, assignments created by this picker are stamped with this ScenarioKey.
   * Omit for the BASELINE (TaskTablePage) flow.
   */
  scenarioKey?: string;
  /**
   * Per-week assignment (2026-06-23): the BST Saturday of the cell being staffed.
   * When set, the picker (a) judges availability for THIS week and (b) defaults to
   * assigning THIS week only, with a "This week / All weeks" scope toggle for
   * multi-week tests. Omit for whole-task assignment (Machine Schedule / Phase 1).
   */
  targetWeekStart?: string;
}

export const InlineAssigneePicker: React.FC<Props> = ({
  task,
  assignees,
  onAssigned,
  onUnassigned,
  // onEscalateToDialog retained on Props for caller compatibility, but the picker
  // no longer auto-escalates to the full dialog — a skill-gate opens the mentor
  // picker inline (one popup, not three).
  machineBookingKey,
  scenarioKey,
  targetWeekStart,
}) => {
  const theme = useTheme();

  // ── Per-week scope (2026-06-23) ────────────────────────────────────────────
  // For a multi-week test, staffing a week cell defaults to THIS week only; the
  // planner can switch to "All weeks" before picking. The weeks come from the
  // booking being staffed (the named machine, else the earliest-starting one).
  const bookingWeeks = useMemo(() => {
    if (!targetWeekStart) return [];
    const list = task.Bookings ?? [];
    if (list.length === 0) return [];
    let target = machineBookingKey
      ? list.find((b) => b.MachineBookingKey === machineBookingKey)
      : undefined;
    if (!target) {
      target = list[0];
      for (const b of list) if (b.StartDate < target.StartDate) target = b;
    }
    return weekStartsInRange(target.StartDate, target.EndDate);
  }, [targetWeekStart, task.Bookings, machineBookingKey]);
  const multiWeek = bookingWeeks.length > 1;
  const [applyAllWeeks, setApplyAllWeeks] = useState(false);
  // The week to write to: this cell's week (default) unless the planner chose "all
  // weeks". Undefined ⇒ span every week (the legacy whole-task behaviour).
  const weekScope = targetWeekStart && !applyAllWeeks ? targetWeekStart : undefined;
  // BUG-2026-0090 — seed from the per-task-type cache so skill levels match the
  // task being assigned (SkillMatrix is keyed by UserKey × TaskTypeKey).
  const taskTypeKey = task.TaskTypeKey;
  const cacheKey = taskTypeKey ?? NO_TASK_TYPE;
  const [users, setUsers] = useState<AssignableUser[]>(
    () => userCache.get(cacheKey)?.users ?? [],
  );
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // BUG-2026-0093 — UserKey currently being removed, so we can disable just
  // that chip's "×" and show its spinner without freezing the whole picker.
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  // Skill-gate #4 — warn before removing the last L3+ supervisor from a task.
  const { guardRemoval, removalConfirm, closeRemovalConfirm } = useRemovalGuard();

  // Inline gate resolution (replaces escalating to the full dialog).
  // `pendingUserPercents` is the assignment being attempted, retried after the
  // user adds a mentor or clicks "Assign anyway".
  const [pendingUserPercents, setPendingUserPercents] = useState<UserPercentEntry[]>([]);
  const [mentorOpen, setMentorOpen] = useState(false);
  const [mentorBlocked, setMentorBlocked] = useState<SkillGateBlockedUser[]>([]);
  const [mentorTaskTypeKey, setMentorTaskTypeKey] = useState<string | null>(null);
  // One-time soft-conflict toast: shown after a successful assign when the picked
  // person is also held in ANOTHER draft plan on these days (a soft, non-blocking clash).
  const [softToast, setSoftToast] = useState<{ name: string; sources: CrossScenarioSource[] } | null>(null);
  // G2-7 (audit #2): non-blocking weekly-overload advisory from the assign
  // response — toast it at the moment of over-assignment (never blocks).
  const [overloadToast, setOverloadToast] = useState<string | null>(null);

  // Lazy-load the assignable list the first time any dropdown is opened.
  // Avoids slamming the API with one call per task row on initial render.
  //
  // BUG-2026-0089 — every row renders its own InlineAssigneePicker with its own
  // `users` state, but the network list is shared via the module-level cache.
  // The first row to open warms the cache AND its own `users`; any row that
  // opens *afterward* used to early-return here (because the cache was now
  // truthy) WITHOUT ever calling setUsers, leaving its local `users` stuck at
  // the empty array it was seeded with at mount time — so the dropdown showed
  // "No assignable users yet" on the 2nd+ task. Fix: when the cache is already
  // warm, sync this row's local state from it (no extra network call); only
  // fetch when the cache is still cold.
  //
  // BUG-2026-0090 — the cache is keyed by task type now, so the sync/fetch
  // targets this task's cache entry; the effect re-runs if the row's task type
  // changes (e.g. the parent swaps the task object).
  useEffect(() => {
    if (!open) return;
    const cached = userCache.get(cacheKey)?.users;
    // Cache already warm (another row of the same task type loaded it first):
    // adopt it locally so this row's dropdown shows the same list + skill levels.
    if (cached) {
      if (users !== cached) setUsers(cached);
      return;
    }
    let cancelled = false;
    setLoadingUsers(true);
    loadAssignableUsersOnce(taskTypeKey)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load users.';
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => { cancelled = true; };
  }, [open, users, cacheKey, taskTypeKey]);

  // RM-5/RM-6 + lab answer Q2 (2026-07-10): continuous tasks staff (and are
  // judged on) EVERY working day of [setup..teardown] — the picker copy +
  // availability projection follow the full-duration model.
  const isContinuousTask = !task.IsPrepStep && (task.RunMode ?? 'attended') === 'continuous';

  // Availability (shared module): each candidate's existing load, so the dropdown
  // shows who's free vs. busy. availabilityUnknown → neutral rows, never a false
  // green "free" (P-18).
  const { relevantWeek, capacity, availabilityUnknown } = useAssigneeCapacity({
    open,
    bookings: task.Bookings,
    machineBookingKey,
    scenarioKey,
    // Availability reflects THIS cell's week (not the booking's first week) so a
    // multi-week test shows the right week's load (#3) — but flipping "All N weeks"
    // re-judges across the WHOLE booking span (P-18), matching what will be written.
    weekStart: applyAllWeeks ? undefined : targetWeekStart,
    // RM-5: continuous tasks judge the full-duration attendance windows
    // (every working day of [setup..teardown] — lab answer Q2, 2026-07-10).
    runMode: task.RunMode ?? null,
    isPrepStep: task.IsPrepStep,
  });

  // Dedup currently-assigned rows by UserKey (multi-week rows produce duplicates)
  const uniqueAssignees = useMemo(() => {
    const seen = new Set<string>();
    const out: TaskAssignee[] = [];
    for (const a of assignees) {
      if (seen.has(a.UserKey)) continue;
      seen.add(a.UserKey);
      out.push(a);
    }
    return out;
  }, [assignees]);

  const assignedKeys = useMemo(
    () => new Set(uniqueAssignees.map((a) => a.UserKey)),
    [uniqueAssignees],
  );

  // Filter out users already on the task so the dropdown only shows *new* picks.
  //
  // BUG-2026-0094 — also hide users who are NOT qualified for this task's task
  // type. "Qualified" = has a recorded SkillMatrix score for this task type
  // (see isQualifiedForTaskType / scheduleQualification.ts). The backend
  // (assignable-users) intentionally LEFT JOINs to keep un-rated users in the
  // list (BUG-2026-0090: they were flagged with a "—" rather than hidden), so
  // the qualification gate lives in the UI. It is SHARED with AssignEmployeesDialog
  // via scheduleQualification.ts so the two assignee surfaces never drift apart
  // — the first fix patched only this inline picker and the dialog kept leaking.
  //   - taskTypeKey set → SkillLevel was requested: drop options with no score.
  //     Levels 1–4 stay (they ARE qualified, just possibly mentor-gated by the
  //     existing skill gate at assignment time).
  //   - taskTypeKey null → no task type, SkillLevel can't be judged: show everyone.
  //
  // IMP2-2 (audit #2) — then RANK instead of listing alphabetically: absent
  // (would hard-409) last, then free-days-on-the-booking desc, then skill desc,
  // then name. While availability is still loading the list ranks by skill +
  // name only (rankAssignees handles the unknown state). Ordering only — the
  // planner still picks (G-1).
  const pickableUsers = useMemo(
    () =>
      rankAssignees(
        users.filter((u) => {
          if (assignedKeys.has(u.AppUserKey)) return false;
          return isQualifiedForTaskType(u, taskTypeKey);
        }),
        capacity,
        !availabilityUnknown,
      ),
    [users, assignedKeys, taskTypeKey, capacity, availabilityUnknown],
  );

  // Whether the loaded list had any qualified candidate at all for this task
  // type (BUG-2026-0094) — drives a clearer empty-state message below.
  const hasQualifiedCandidate = useMemo(
    () => listHasQualifiedCandidate(users, taskTypeKey),
    [users, taskTypeKey],
  );

  // Core assign call. On a skill-gate it opens the mentor picker INLINE and
  // stashes the attempted rows so the resolver can retry — no hand-off to the
  // full dialog. An absence-day conflict (Q3B) is a HARD block: the server's
  // message shows verbatim in the red caption and there is nothing to retry.
  const doAssign = useCallback(
    async (
      userPercents: UserPercentEntry[],
      confirmSkillGate = false,
    ): Promise<boolean> => {
      if (userPercents.length === 0) return false;
      setSaving(true);
      setError(null);
      try {
        const result = await bulkAssignFromTask({
          scheduleTaskKey: task.ScheduleTaskKey,
          userPercents,
          // W2-8 (warn-only): "Assign anyway" — skip the advisory mentor gate.
          ...(confirmSkillGate ? { confirmSkillGate: true } : {}),
          ...(machineBookingKey != null ? { machineBookingKey } : {}),
          ...(scenarioKey !== undefined ? { scenarioKey } : {}),
          // Per-week scope: assign only this cell's week unless "all weeks" is chosen (#2).
          ...(weekScope ? { weekStart: weekScope } : {}),
        });
        // G2-7: surface the non-blocking weekly-overload advisory (if any).
        setOverloadToast(weekOverloadHint(result?.weekOverloaded));
        await onAssigned();
        return true;
      } catch (err) {
        if (err instanceof SkillGateError) {
          setPendingUserPercents(userPercents);
          setMentorBlocked(err.blockedUsers);
          setMentorTaskTypeKey(err.taskTypeKey);
          setMentorOpen(true);
        } else if (err instanceof AbsenceDayConflictError) {
          // Q3B: verbatim server message, no Save-anyway.
          setError(err.message);
        } else {
          const axiosBody = (err as { response?: { data?: { error?: string } } })
            ?.response?.data?.error;
          const fallback = err instanceof Error ? err.message : 'Failed to assign.';
          setError(axiosBody || fallback);
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [task.ScheduleTaskKey, onAssigned, machineBookingKey, scenarioKey, weekScope],
  );

  const handleSelect = useCallback(
    async (user: AssignableUser | null) => {
      if (!user || saving) return;
      // Wave 3: presence — always 100 (the server pins it; tier defaults died).
      const assigned = await doAssign([{ userKey: user.AppUserKey, percent: 100 }]);
      // After a clean assign, if this person is also held in ANOTHER plan on these
      // days, raise the one-time soft-conflict heads-up (non-blocking). The soft load
      // was loaded with the dropdown, so it reflects the OTHER plans, not this assign.
      if (assigned) {
        const load = capacity.get(user.AppUserKey);
        if (load && load.softHeldOnBookingDays) {
          setSoftToast({ name: user.DisplayName, sources: load.softSources });
        }
      }
    },
    [doAssign, saving, capacity],
  );

  // Mentor chosen → retry with them added. Wave 3: the mentor is a presence
  // like everyone (the 25%-oversight convention died with the percent model).
  const handleAddMentor = useCallback(
    (mentor: EligibleMentor) => {
      setMentorOpen(false);
      const withMentor = pendingUserPercents.some((p) => p.userKey === mentor.AppUserKey)
        ? pendingUserPercents
        : [...pendingUserPercents, { userKey: mentor.AppUserKey, percent: 100 }];
      void doAssign(withMentor);
    },
    [doAssign, pendingUserPercents],
  );

  // W2-8 (warn-only): proceed WITHOUT a mentor — retry with the gate bypassed.
  const handleAssignAnyway = useCallback(() => {
    setMentorOpen(false);
    void doAssign(pendingUserPercents, true);
  }, [doAssign, pendingUserPercents]);

  // BUG-2026-0093 — remove a person from the task. Soft-deletes ALL of their
  // per-week EmployeeAssignment rows (a user can have one row per week), then
  // asks the parent to refresh this row's chips.
  const handleUnassign = useCallback(
    (userKey: string) => {
      if (removingKey) return;
      const displayName =
        assignees.find((a) => a.UserKey === userKey)?.displayName ?? 'This person';
      // Skill-gate #4 — if this is the last supervisor, warn first; the actual
      // removal runs immediately when safe, or on confirm when it would strand.
      void guardRemoval(task.ScheduleTaskKey, userKey, displayName, scenarioKey ?? undefined, async () => {
        setRemovingKey(userKey);
        setError(null);
        try {
          await unassignUserFromTask(assignees, userKey);
          await onUnassigned();
        } catch (err) {
          const axiosBody = (err as { response?: { data?: { error?: string } } })
            ?.response?.data?.error;
          const fallback = err instanceof Error ? err.message : 'Failed to remove.';
          setError(axiosBody || fallback);
        } finally {
          setRemovingKey(null);
        }
      });
    },
    [assignees, removingKey, onUnassigned, guardRemoval, task.ScheduleTaskKey, scenarioKey],
  );

  const disabled = task.Bookings.length === 0;

  // Tooltip for the disabled state — same wording as the old icon button
  const disabledHint = 'Place this task on a machine first (see the Selected Machine column)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      {/* Currently-assigned chips */}
      {uniqueAssignees.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
          {uniqueAssignees.map((a) => {
            const color =
              a.Status === 'Committed'
                ? theme.schedule.committed
                : theme.schedule.employeeAlloc;
            const isRemoving = removingKey === a.UserKey;
            return (
              <Tooltip
                key={a.UserKey}
                title={
                  isRemoving
                    ? 'Removing…'
                    : `${a.displayName} · ${a.Status}${a.tier ? ` · ${a.tier}` : ''} — click × to remove`
                }
                arrow
              >
                <Chip
                  label={a.displayName}
                  size="small"
                  // BUG-2026-0093 — deletable chip: the × soft-deletes all of
                  // this user's per-week assignment rows for the task.
                  onDelete={removingKey ? undefined : () => void handleUnassign(a.UserKey)}
                  deleteIcon={
                    isRemoving ? (
                      <CircularProgress size={11} sx={{ color }} />
                    ) : (
                      <Cancel aria-label={`Remove ${a.displayName}`} />
                    )
                  }
                  sx={{
                    fontSize: '0.68rem',
                    height: 20,
                    maxWidth: '100%',
                    backgroundColor: alpha(color, 0.12),
                    color,
                    fontWeight: 600,
                    '& .MuiChip-label': {
                      px: 0.75,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: alpha(color, 0.7),
                      fontSize: 14,
                      '&:hover': { color },
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}

      {/* Per-week scope (multi-week tests only): assign just this week, or all weeks. */}
      {targetWeekStart && multiWeek && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Add to this week only" arrow>
            <Chip
              label="This week"
              size="small"
              onClick={() => setApplyAllWeeks(false)}
              variant={applyAllWeeks ? 'outlined' : 'filled'}
              sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                cursor: 'pointer',
                ...(applyAllWeeks
                  ? {}
                  : { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.16), color: theme.schedule.employeeAlloc }),
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              isContinuousTask
                ? 'Adds to every working day of the run, setup through teardown — the machine keeps running unstaffed on weekends/holidays'
                : `Add to all ${bookingWeeks.length} weeks of this test`
            }
            arrow
          >
            <Chip
              label={isContinuousTask ? 'All run weeks' : `All ${bookingWeeks.length} weeks`}
              size="small"
              onClick={() => setApplyAllWeeks(true)}
              variant={applyAllWeeks ? 'filled' : 'outlined'}
              sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                cursor: 'pointer',
                ...(applyAllWeeks
                  ? { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.16), color: theme.schedule.employeeAlloc }
                  : {}),
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Inline picker */}
      <Tooltip title={disabled ? disabledHint : ''} arrow disableHoverListener={!disabled}>
        <Box sx={{ width: '100%' }}>
          <Autocomplete<AssignableUser, false, false, false>
            size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={pickableUsers}
            getOptionLabel={(u) => u.DisplayName}
            isOptionEqualToValue={(a, b) => a.AppUserKey === b.AppUserKey}
            // IMP2-2 — a dated absence on the booking's days makes the save a
            // guaranteed hard 409 (Q3B, no bypass), so the option is disabled;
            // its row still shows the "unavailable (day off)" reason. Never
            // disabled while availability is unknown (save-time gate rules).
            getOptionDisabled={(u) =>
              isPickBlockedByAbsence(capacity.get(u.AppUserKey), !availabilityUnknown)
            }
            loading={loadingUsers}
            disabled={disabled || saving}
            value={null}
            onChange={(_e, value) => {
              void handleSelect(value);
            }}
            blurOnSelect
            clearOnBlur
            clearOnEscape
            noOptionsText={
              loadingUsers
                ? 'Loading…'
                : users.length === 0
                  ? 'No assignable users yet'
                  : // BUG-2026-0094 — list loaded but nobody is qualified for
                    // this task type (all candidates lacked a SkillMatrix score).
                    !hasQualifiedCandidate
                    ? 'No qualified users for this task type'
                    : 'No more users to assign'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  uniqueAssignees.length > 0 ? 'Add another…' : 'Assign…'
                }
                size="small"
                inputProps={{
                  ...params.inputProps,
                  style: { fontSize: '0.78rem', padding: '4px 6px' },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {(loadingUsers || saving) && <CircularProgress size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingY: 0.25,
                    fontSize: '0.78rem',
                  },
                }}
              />
            )}
            // Wider popup so the single-row option (name · skill · tier · load)
            // has room for the availability column on the right.
            componentsProps={{ paper: { sx: { minWidth: 360 } } }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 0.6 }}>
                <AssigneeOptionRow
                  displayName={option.DisplayName}
                  tier={option.Tier}
                  skillLevel={option.SkillLevel}
                  showSkill={Boolean(taskTypeKey)}
                  load={capacity.get(option.AppUserKey) ?? null}
                  showLoad={Boolean(relevantWeek)}
                  loadUnknown={availabilityUnknown}
                />
              </Box>
            )}
          />
        </Box>
      </Tooltip>

      {/* RM-5 + lab answer Q2 (2026-07-10): the availability column judges (and
          the save writes) every working day of the run — say so in one line. */}
      {isContinuousTask && (
        <Tooltip title={CONTINUOUS_STAFFING_HINT} arrow>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '0.62rem', lineHeight: 1.2 }}
          >
            Staffed every working day of the run (continuous 24/7 test).
          </Typography>
        </Tooltip>
      )}

      {error && (
        <Typography
          variant="caption"
          sx={{
            color: theme.schedule.conflictHard,
            fontSize: '0.65rem',
            lineHeight: 1.1,
          }}
        >
          {error}
        </Typography>
      )}

      {/* Skill-gate #4 — warn-and-confirm when removing the last supervisor. */}
      <ConfirmDialog state={removalConfirm} onClose={closeRemovalConfirm} />

      {/* Inline gate resolution — opens directly from the dropdown, no
          intermediate "Assign Employees" dialog. */}
      <MentorRequiredModal
        open={mentorOpen}
        taskTypeKey={mentorTaskTypeKey}
        blockedUsers={mentorBlocked}
        onAddMentor={handleAddMentor}
        onAssignAnyway={handleAssignAnyway}
        onClose={() => setMentorOpen(false)}
      />

      {/* One-time, dismissible soft-conflict heads-up — the assignment was allowed
          (it's only tentative elsewhere), but the planner should know about the clash. */}
      <Snackbar
        open={!!softToast}
        autoHideDuration={9000}
        onClose={() => setSoftToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSoftToast(null)}
          sx={{ maxWidth: 520, alignItems: 'flex-start' }}
        >
          {softToast &&
            `Heads up — ${softToast.name} is also held in another plan on these days` +
              (softToast.sources.length ? ` (${softToast.sources.map(formatCrossScenarioSource).join('; ')})` : '') +
              `. That plan is tentative, so booking here is fine — just be aware there's a possible clash.`}
        </Alert>
      </Snackbar>

      {/* G2-7 — non-blocking weekly-overload advisory: the assignment saved, but
          this person now carries more task-days than the week holds. */}
      <Snackbar
        open={!!overloadToast}
        autoHideDuration={9000}
        onClose={() => setOverloadToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setOverloadToast(null)}
          sx={{ maxWidth: 520, alignItems: 'flex-start' }}
        >
          {overloadToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};
